import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Package, Truck, Info } from "lucide-react";

/**
 * ShipmentPreview — shows the customer *before checkout* whether their box
 * will arrive in one shipment or two (split-shipping).
 *
 * Only appears when:
 *  - The drop has a master_box template linked (backend guard)
 *  - The parent has provided either a `state` (2-letter code) or a full 5-digit `zip`
 *  - The plan resolves to 2+ warehouses
 *
 * Silently hidden in the single-shipment case — this UI is a warning, not a
 * primary indicator.
 *
 * Props:
 *  - slug: drop slug
 *  - state?: destination state code
 *  - zip?: destination 5-digit ZIP
 *  - quantity?: box count (default 1)
 */
export default function ShipmentPreview({ slug, state, zip, quantity = 1 }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug || (!state && !zip)) { setPreview(null); return; }
    setError(false);
    api.post(`/drops/${encodeURIComponent(slug)}/shipment-preview`, {
      state: state || undefined,
      zip: zip || undefined,
      quantity,
    })
      .then(r => setPreview(r.data))
      .catch(() => setError(true));
  }, [slug, state, zip, quantity]);

  if (error || !preview || !preview.is_split) return null;

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3.5" data-testid="shipment-preview">
      <div className="flex items-start gap-2">
        <Truck size={16} className="text-amber-700 flex-shrink-0 mt-0.5"/>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900" data-testid="shipment-preview-headline">
            Heads up — this box arrives in {preview.shipment_count} shipments
          </p>
          <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
            Some items in this drop aren&apos;t stocked at your nearest warehouse, so we&apos;ll ship from two locations.
            {preview.flat_shipping_note && <> <strong>{preview.flat_shipping_note}</strong></>}
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {preview.shipments.map((s) => (
              <li key={s.warehouse_id} className="flex items-start gap-2 text-xs" data-testid={`shipment-leg-${s.warehouse_id}`}>
                <Package size={12} className="text-amber-700 mt-0.5 flex-shrink-0"/>
                <div className="min-w-0">
                  <span className="font-semibold text-amber-900">
                    Shipment from {s.warehouse_name}
                    {s.warehouse_state && <span className="opacity-70"> ({s.warehouse_state})</span>}
                    :
                  </span>{" "}
                  <span className="text-amber-900/80">
                    {s.items.map(i => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ""}`).join(", ")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-amber-800/70 mt-2.5 inline-flex items-center gap-1">
            <Info size={10}/> Shipments may arrive on different days depending on carrier transit.
          </p>
        </div>
      </div>
    </div>
  );
}
